/**
 * Automations — Step 32's admin surface.
 *
 * Deliberately a LIST and an EXECUTION LOG, not a builder. The visual builder
 * is Step 89, and because the engine stores a graph from its first commit
 * (Kai's ruling), that builder is a renderer over data that already exists
 * rather than a rewrite of anything here.
 *
 * The list answers the two questions people actually ask about a rules engine:
 * **what is switched on**, and **why did that ticket move**. The second is why
 * the execution log is on the same page rather than buried — an automation
 * nobody can audit is one nobody dares switch on.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  PauseCircle,
  Plus,
  RotateCcw,
  Trash2,
  Workflow,
} from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { relativeTime } from '../../lib/time';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import {
  RuleBuilder,
  TRIGGERS,
  describeGraph,
  emptyGraph,
  type Graph,
} from '../../features/automations/RuleBuilder';

interface Rule {
  id: string;
  name: string;
  description: string | null;
  trigger: string;
  status: string;
  position: number;
  lastRunAt: number | null;
  graph: { nodes: { id: string; type: string }[]; edges: unknown[] };
}
interface Run {
  id: string;
  ruleId: string;
  entityId: string;
  trigger: string;
  depth: number;
  status: string;
  detail: string | null;
  steps: string[];
  startedAt: number;
}

const TRIGGER_LABEL: Record<string, string> = {
  'ticket.created': 'Ticket created',
  'ticket.updated': 'Ticket updated',
  'ticket.status_changed': 'Status changed',
  'ticket.priority_changed': 'Priority changed',
  'ticket.assigned': 'Assigned',
  'requester.replied': 'Requester replied',
  'agent.replied': 'Agent replied',
  'ticket.note_added': 'Private note added',
  'catalogue.request_submitted': 'Service request submitted',
};

const api = {
  list: () => apiFetch<{ entries: Rule[] }>('/api/admin/automations'),
  runs: () => apiFetch<{ entries: Run[] }>('/api/admin/automations/runs'),
  activate: (id: string) => apiSend<{ errors?: string[] }>(`/api/admin/automations/${id}/activate`, 'POST'),
  deactivate: (id: string) => apiSend(`/api/admin/automations/${id}/deactivate`, 'POST'),
  remove: (id: string) => apiSend(`/api/admin/automations/${id}`, 'DELETE'),
  restore: (id: string) => apiSend(`/api/admin/automations/${id}/restore`, 'POST'),
  reorder: (ids: string[]) => apiSend('/api/admin/automations/reorder', 'POST', { ids }),
  trash: () => apiFetch<{ entries: Rule[] }>('/api/admin/automations?trashed=1'),
};

/** Run status colouring. `partial` earns amber on its own: "it ran but
 *  something inside failed" is the outcome that goes unnoticed for months if
 *  it is collapsed into success. */
function RunBadge({ status }: { status: string }) {
  if (status === 'success')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> success
      </span>
    );
  if (status === 'partial')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
        <AlertTriangle className="h-3.5 w-3.5" /> partial
      </span>
    );
  if (status === 'failed')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" /> failed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <PauseCircle className="h-3.5 w-3.5" /> skipped
    </span>
  );
}

export function AutomationsPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [tab, setTab] = useUrlState('tab', 'rules');
  const [actionError, setActionError] = useState<string | null>(null);
  // Step 89 — WHICH rule is open belongs in the URL; the draft does not.
  const [editId, setEditId] = useUrlState('rule', null);
  const [draft, setDraft] = useState<{
    id: string;
    name: string;
    trigger: string;
    graph: Graph;
  } | null>(null);

  const rules = useQuery({ queryKey: qk.automations, queryFn: api.list });
  // The builder's pickers need real groups, agents and statuses — an action
  // offering an id nobody can read is an action nobody will configure.
  const meta = useQuery({
    queryKey: qk.automationMeta,
    queryFn: () =>
      apiFetch<{
        groups: { id: string; name: string }[];
        agents: { id: string; name: string | null; email: string }[];
        statuses: { key: string; name: string }[];
        subflows: { id: string; name: string }[];
        items: { id: string; name: string }[];
        departments: { id: string; name: string }[];
        boards: { id: string; name: string }[];
        collections: { key: string; label: string; needsFieldKey: boolean }[];
      }>('/api/admin/automations/meta'),
    enabled: !!editId,
  });
  const trash = useQuery({
    queryKey: qk.automationTrash,
    queryFn: api.trash,
    enabled: tab === 'trash',
  });
  const runs = useQuery({
    queryKey: qk.automationRuns,
    queryFn: api.runs,
    enabled: tab === 'log',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.automations });
    void queryClient.invalidateQueries({ queryKey: qk.automationRuns });
    void queryClient.invalidateQueries({ queryKey: qk.automationTrash });
  };

  /** Open a rule in the builder. A new rule starts from the smallest graph
   *  that validates, so the first thing you see is already legal. */
  const open = (r?: Rule) => {
    setDraft(
      r
        ? { id: r.id, name: r.name, trigger: r.trigger, graph: r.graph as unknown as Graph }
        : { id: '', name: '', trigger: 'ticket.created', graph: emptyGraph() },
    );
    setEditId(r?.id ?? 'new');
    setActionError(null);
  };
  const close = () => {
    // Clear the guard FIRST. `editId` lives in the URL, so closing the editor
    // is a navigation — and without this it trips the unsaved-changes blocker
    // on Save (where nothing is unsaved) and on Cancel (where discarding is
    // exactly what was asked for).
    clearDirty();
    setDraft(null);
    setEditId(null);
    setActionError(null);
  };
  /**
   * Dirty against the SERVER's copy, not merely "the editor is open" — a Save
   * that lights up on an untouched rule is the same lie the save-state audit
   * exists to catch, and I wrote it again here.
   *
   * A NEW rule is dirty as soon as it has a name; there is nothing to compare
   * it against.
   */
  const serverRule = draft?.id ? rules.data?.entries.find((r) => r.id === draft.id) : undefined;
  const dirty =
    !!draft &&
    (!draft.id
      ? !!draft.name.trim()
      : !serverRule ||
        draft.name !== serverRule.name ||
        draft.trigger !== serverRule.trigger ||
        JSON.stringify(draft.graph) !== JSON.stringify(serverRule.graph));
  const clearDirty = useDirty(dirty);

  const save = useMutation({
    mutationFn: () => {
      const body = { name: draft!.name, trigger: draft!.trigger, graph: draft!.graph };
      return draft!.id
        ? apiSend(`/api/admin/automations/${draft!.id}`, 'PATCH', body)
        : apiSend<{ id: string }>('/api/admin/automations', 'POST', body);
    },
    onSuccess: (res) => {
      if (!res.ok) {
        setActionError(res.error ?? 'Could not save');
        return;
      }
      close();
      invalidate();
    },
  });

  const activate = useMutation({
    mutationFn: (id: string) => api.activate(id),
    onSuccess: (res) => {
      if (!res.ok) {
        // The activation refusal names EVERY problem, not just the first —
        // fixing one at a time is how a five-minute edit becomes an hour.
        setActionError(res.body?.errors?.join(' · ') ?? res.error ?? 'Could not activate');
        return;
      }
      setActionError(null);
      invalidate();
    },
  });

  if (rules.isPending) return <Skeleton className="m-6 h-64 w-full max-w-4xl" />;
  if (rules.isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load automations</p>
        <p className="text-xs text-destructive/80">{(rules.error as Error).message}</p>
        <button
          onClick={() => rules.refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  /**
   * Grouped by KIND, event-based before scheduled.
   *
   * The reference groups by entity AND kind; we have one entity so far, and
   * the distinction that actually matters is event versus scheduled — a
   * scheduled rule SCANS on a cadence, an event rule REACTS, and confusing
   * the two is how someone writes an hourly rule expecting it to fire on save.
   *
   * Done by ORDERING rather than by separate lists, so reorder still operates
   * on one contiguous sequence: rules run in a single global order, and a UI
   * that split them into two lists would imply two orders.
   */
  const entries = [...rules.data!.entries].sort((a, b) => {
    const ka = a.trigger.startsWith('schedule.') ? 1 : 0;
    const kb = b.trigger.startsWith('schedule.') ? 1 : 0;
    return ka - kb || a.position - b.position;
  });

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <Workflow className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Automations</h1>
        <div className="flex-1" />
        {!draft && (
          <Button size="sm" onClick={() => open()}>
            <Plus /> New automation
          </Button>
        )}
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Rules run in order, one at a time per ticket. A rule that changes a ticket can trigger further rules —
        up to a depth limit, so a loop stops itself and says so in the log.
      </p>

      {!draft && (
        <div className="mb-4 flex gap-1 border-b">
          {(['rules', 'log', 'trash'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                'border-b-2 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
                (tab === t
                  ? 'border-brand font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground')
              }
            >
              {t === 'rules' ? 'Rules' : t === 'log' ? 'Execution log' : 'Trash'}
            </button>
          ))}
        </div>
      )}

      {actionError && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2.5">
          <p className="text-xs font-medium text-destructive">Invalid automation configuration</p>
          <p className="mt-0.5 text-xs text-destructive/80">{actionError}</p>
        </div>
      )}

      {draft ? (
        <div className="space-y-4 rounded-lg border bg-card p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <Input
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Reopen when the requester replies"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Trigger</label>
              <Select value={draft.trigger} onChange={(e) => setDraft({ ...draft, trigger: e.target.value })}>
                {TRIGGERS.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* The SUMMARY panel — the rule read back as a sentence, live.
              Copied deliberately: a rules engine you cannot read at a glance
              is one nobody trusts, and this is the cheapest possible defence
              against a rule that does something other than its author meant. */}
          <div className="rounded-md border bg-muted/30 p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Summary</p>
            <p className="text-sm leading-relaxed">{describeGraph(draft.graph, draft.trigger)}</p>
          </div>

          <RuleBuilder
            graph={draft.graph}
            trigger={draft.trigger}
            onChange={(g) => setDraft({ ...draft, graph: g })}
            groups={meta.data?.groups ?? []}
            agents={meta.data?.agents ?? []}
            statuses={meta.data?.statuses ?? []}
            subflows={meta.data?.subflows ?? []}
            collections={meta.data?.collections ?? []}
            items={meta.data?.items ?? []}
            departments={meta.data?.departments ?? []}
            boards={meta.data?.boards ?? []}
          />

          <div className="flex items-center justify-end gap-2 border-t pt-4">
            {/* Said before it happens, not discovered afterwards. */}
            <span className="mr-auto text-xs text-muted-foreground">
              Saving an active rule returns it to draft until you activate it again.
            </span>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button disabled={!dirty || !draft.name.trim() || save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      ) : tab === 'rules' ? (
        entries.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center">
            <p className="text-sm text-muted-foreground">No automations yet.</p>
            {/* Honest about what exists: the engine is real and usable via the
                API, and the visual builder is a later step. Saying so beats a
                "New rule" button that opens nothing. */}
            <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-muted-foreground">
              Build one with the visual editor, or start from one of the seeded rules — they ship switched off
              so nothing fires until you have read it.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((r, i) => (
              <div key={r.id}>
                {(i === 0 || ruleKind(entries[i - 1]!.trigger) !== ruleKind(r.trigger)) && (
                  <p className="mb-2 mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {ruleKind(r.trigger)}
                  </p>
                )}
                <div className="rounded-lg border bg-card p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{r.name}</span>
                    {r.status === 'active' ? (
                      <Badge variant="outline" className="gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                      </Badge>
                    ) : (
                      <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                        Draft
                      </Badge>
                    )}
                    <div className="flex-1" />
                    {/* Reorder is EXPLICIT, never implicit created_at. Rules run
                      top to bottom and the first match acts first, so "which
                      rule wins" is decided here and nowhere else. */}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={i === 0}
                      aria-label={`Move ${r.name} up`}
                      onClick={() => {
                        const ids = entries.map((x) => x.id);
                        [ids[i - 1], ids[i]] = [ids[i]!, ids[i - 1]!];
                        void api.reorder(ids).then(invalidate);
                      }}
                    >
                      <ChevronUp />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={i === entries.length - 1}
                      aria-label={`Move ${r.name} down`}
                      onClick={async () => {
                        const ids = entries.map((x) => x.id);
                        [ids[i], ids[i + 1]] = [ids[i + 1]!, ids[i]!];
                        void api.reorder(ids).then(invalidate);
                      }}
                    >
                      <ChevronDown />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => open(r)}>
                      Edit
                    </Button>
                    {r.status === 'active' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => api.deactivate(r.id).then(invalidate)}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => activate.mutate(r.id)}>
                        Activate
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-destructive/40 text-destructive hover:bg-destructive/10"
                      onClick={async () => {
                        if (
                          !(await confirm({
                            title: `Move “${r.name}” to trash?`,
                            destructive: true,
                            confirmLabel: 'Move to trash',
                          }))
                        )
                          return;
                        await api.remove(r.id);
                        invalidate();
                      }}
                      aria-label={`Trash ${r.name}`}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    When <span className="font-medium">{TRIGGER_LABEL[r.trigger] ?? r.trigger}</span> ·{' '}
                    {r.graph.nodes.filter((n) => n.type === 'condition').length} condition(s) ·{' '}
                    {r.graph.nodes.filter((n) => n.type === 'action').length} action(s)
                    {r.lastRunAt ? ` · last ran ${relativeTime(r.lastRunAt)}` : ' · never run'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === 'trash' ? (
        trash.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : (trash.data?.entries.length ?? 0) === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
            Nothing in the trash.
          </div>
        ) : (
          <div className="space-y-2">
            {/* 14-day retention, copied from the reference: long enough that
                "we turned that off last week, put it back" works, short
                enough that the trash does not become a second, invisible
                rule list. The sweep runs in the engine. */}
            <p className="text-xs text-muted-foreground">
              Trashed rules are kept for 14 days, then permanently removed. Restoring returns a rule to draft
              — never straight back to active.
            </p>
            {(trash.data?.entries ?? []).map((r) => (
              <div key={r.id} className="flex items-center gap-2 rounded-md border bg-card p-3">
                <span className="min-w-0 flex-1 truncate text-sm">{r.name}</span>
                <Button size="sm" variant="outline" onClick={() => api.restore(r.id).then(invalidate)}>
                  <RotateCcw /> Restore
                </Button>
              </div>
            ))}
          </div>
        )
      ) : runs.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : (runs.data?.entries.length ?? 0) === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          Nothing has run yet.
        </div>
      ) : (
        <div className="space-y-2">
          {(runs.data?.entries ?? []).map((run) => (
            <div key={run.id} className="rounded-md border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <RunBadge status={run.status} />
                <span className="text-xs text-muted-foreground">
                  {TRIGGER_LABEL[run.trigger] ?? run.trigger}
                </span>
                {/* Depth is shown because it is the loop-safety budget: a run
                    at depth 2 was caused by another automation, and that is
                    the single most useful fact when a rule surprises you. */}
                {run.depth > 0 && (
                  <Badge variant="outline" className="text-[10px]">
                    depth {run.depth}
                  </Badge>
                )}
                <div className="flex-1" />
                <span className="text-xs text-muted-foreground">{relativeTime(run.startedAt)}</span>
              </div>
              {run.steps.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {run.steps.map((s, i) => (
                    <li key={i} className="text-xs text-muted-foreground">
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Grouped by KIND, in one list — the reference groups event-based, scheduled
 * and subflows the same way.
 *
 * By ORDERING, deliberately, not by separate lists: rules run in one global
 * order and two lists would imply two orders. Step 90 added the third kind,
 * which is the one that finally makes the grouping earn its place.
 */
function ruleKind(trigger: string): string {
  if (trigger === 'subflow.called') return 'Subflows';
  if (trigger.startsWith('schedule.')) return 'Scheduled';
  return 'Event-based';
}
